import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TrendingUp, AlertTriangle, Clock } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function VendorPerformance() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(false);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const res = await api.get("/staff/procurement/vendor-performance", { params: { days: d } });
      setData(res.data);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, []);

  const vendors = data?.vendors || [];

  return (
    <div data-testid="vendor-performance-page">
      <div className="eyebrow">Procurement · Insights</div>
      <h1 className="mt-2 text-4xl">Vendor performance</h1>
      <p className="mt-2 text-sm text-[hsl(var(--ink-soft))] max-w-2xl">
        Per-vendor scorecard for the last {days} days. On-time % is based on GRN received-date vs PO expected-delivery-date;
        over-receipt % counts lines where received qty exceeded ordered; rate variance is the mean |Δ%| between GRN rate and PO rate.
      </p>
      <div className="mt-4 flex items-center gap-2">
        {[30, 60, 90, 180, 365].map((d) => (
          <button key={d} data-testid={`vp-range-${d}`} onClick={() => { setDays(d); load(d); }} className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full ${days === d ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "border border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]"}`}>{d}d</button>
        ))}
      </div>

      <div className="mt-6 border border-[hsl(var(--rule))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-2))] text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            <tr>
              <th className="p-3 text-left">Vendor</th>
              <th className="p-3 text-right">POs</th>
              <th className="p-3 text-right">GRNs</th>
              <th className="p-3 text-right">On-time %</th>
              <th className="p-3 text-right">Over-receipt %</th>
              <th className="p-3 text-right">Rate variance</th>
              <th className="p-3 text-right">Total spend</th>
              <th className="p-3 text-right">Outstanding</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--rule))]">
            {loading && <tr><td colSpan={8} className="p-8 text-center text-[hsl(var(--ink-soft))]">Loading…</td></tr>}
            {!loading && vendors.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[hsl(var(--ink-soft))]">No vendor activity in this range.</td></tr>}
            {!loading && vendors.map((v) => {
              const otBad = v.on_time_pct !== null && v.on_time_pct < 70;
              const overBad = v.over_receipt_pct > 20;
              const rateBad = v.rate_variance_pct > 10;
              return (
                <tr key={v.vendor_id} data-testid={`vp-row-${v.vendor_id}`}>
                  <td className="p-3">
                    <div className="font-semibold">{v.name}</div>
                    <div className="text-[10px] text-[hsl(var(--ink-soft))]">{v.category || "—"}</div>
                  </td>
                  <td className="p-3 text-right">{v.po_count}</td>
                  <td className="p-3 text-right">{v.grn_count}</td>
                  <td className={`p-3 text-right ${otBad ? "text-[hsl(6_62%_45%)] font-semibold" : ""}`}>{v.on_time_pct == null ? "—" : `${v.on_time_pct}%`}</td>
                  <td className={`p-3 text-right ${overBad ? "text-[hsl(35_75%_38%)] font-semibold" : ""}`}>{v.over_receipt_pct}%</td>
                  <td className={`p-3 text-right ${rateBad ? "text-[hsl(6_62%_45%)] font-semibold" : ""}`}>{v.rate_variance_pct}%</td>
                  <td className="p-3 text-right">{inr(v.total_spend)}</td>
                  <td className={`p-3 text-right ${Number(v.outstanding) > 0 ? "text-[hsl(6_62%_45%)] font-semibold" : "text-[hsl(var(--forest))]"}`}>{inr(v.outstanding)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
